import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export const branchShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['default', 'primary', 'secondary', 'info', 'success', 'warning', 'error']),
});

export const stageShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  branches: PropTypes.arrayOf(branchShape),
});

const LINE_COLOR = 'divider';
const RAIL_OFFSET = 2.5;

function StageNode({ index, label, description }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, px: 1.5, py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
        <Chip label={index} size="small" color="primary" sx={{ height: 20, fontSize: 12 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
}

StageNode.propTypes = {
  index: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

function Branch({ label, description, tone }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.75 }}>
      <Box
        sx={{
          width: 16,
          height: 2,
          mt: 1.1,
          ml: -2,
          bgcolor: LINE_COLOR,
          flexShrink: 0,
        }}
      />
      <Chip label={label} size="small" color={tone} variant="outlined" sx={{ flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary" sx={{ pt: 0.4 }}>
        {description}
      </Typography>
    </Stack>
  );
}

Branch.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tone: PropTypes.string,
};

export default function HowItWorksFlow({ stages }) {
  return (
    <Box>
      {stages.map((stage, index) => {
        const branches = stage.branches || [];
        const isLast = index === stages.length - 1;

        return (
          <Box key={stage.key}>
            <StageNode index={index + 1} label={stage.label} description={stage.description} />

            {(branches.length > 0 || !isLast) && (
              <Box
                sx={{
                  ml: RAIL_OFFSET,
                  pl: 2,
                  py: branches.length > 0 ? 0.5 : 0,
                  borderLeft: '2px solid',
                  borderColor: LINE_COLOR,
                  minHeight: 16,
                }}
              >
                {branches.map((branch) => (
                  <Branch
                    key={branch.label}
                    label={branch.label}
                    description={branch.description}
                    tone={branch.tone}
                  />
                ))}
              </Box>
            )}

            {!isLast && (
              <ArrowDropDownIcon
                sx={{ display: 'block', ml: RAIL_OFFSET, transform: 'translateX(-9px)' }}
                color="disabled"
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

HowItWorksFlow.propTypes = {
  stages: PropTypes.arrayOf(stageShape).isRequired,
};
